<script setup>
import { ref, computed } from 'vue'

import { DxPopup } from 'devextreme-vue/popup'
import { DxForm } from 'devextreme-vue/form'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxFileUploader } from 'devextreme-vue/file-uploader'

import { QStepper, QStep } from 'quasar'

import { getI18n } from '@/plugins/i18n'
import { usePortalStore } from '@/stores/portal.store'
import { registryApi } from '@/apis/registry.api'

import { useLookup } from '@/composables/data-grid'
import { createFieldMappings, getMappingFields, getMappingLayout, isFieldMapped } from '@/composables/registry/import-field-mapping'
import ImportFieldMapping from '@/components/registry/ImportFieldMapping.vue'
import ImportFieldValueEditor from '@/components/registry/ImportFieldValueEditor.vue'
import { useLookupModelMap } from '@/composables/registry/import-export-lookup'

const { getEditorOptionsForField, loadFieldLookupsAsync } = useLookupModelMap()

const props = defineProps({
  visible: { type: Boolean, default: false },
})

const emit = defineEmits(['update:visible', 'created'])

const i18n = getI18n()
const portalStore = usePortalStore()

const step = ref(1)
const saving = ref(false)

const profileData = ref({
  operation_type: portalStore.operationTypeEnum.IMPORT,
  track_type: null,
})

const fileConfigData = ref({
  file_format: portalStore.importFileFormatEnum.CSV,
  sheet_name: null,
  thousand_separator: null,
  decimal_separator: null,
  text_qualifier: '',
  csv_delimiter: portalStore.importCsvDelimiterEnum.SEMICOLON,
  date_format: portalStore.importDateFormatEnum.AUTO,
  encoding: portalStore.importEncodingEnum.UTF8,
  header_row_number: 1,
  search_key: portalStore.importSearchKeyEnum.CODE,
  existing_record_mode: portalStore.importExistingRecordModeEnum.NEW_AND_UPDATE,
  update_mode: portalStore.importUpdateModeEnum.FILLED_ONLY,
})
const uploadedFile = ref(null)
const searchKeyOptions = computed(() => portalStore.getLocalizedImportSearchKeys(profileData.value.track_type))
const isSearchKeyValid = computed(() => searchKeyOptions.value.some(option => option.id === fileConfigData.value.search_key))
const textQualifiers = computed(() => [
  { value: '"', name: '""' },
  { value: "'", name: "'" },
  { value: '', name: i18n.t('common.noTextQualifier') },
])
const filePreviewReady = ref(false)
const loadingFilePreview = ref(false)
let filePreviewRequestId = 0
const csvPreviewColumns = ref([])
const sheetNames = ref([])

const importableFields = ref([])
const fieldMappings = ref([])
const fieldMappingRef = ref(null)
const loadingFields = ref(false)
let fieldsRequestId = 0

const transcodableFields = computed(() => {
  const mappingByKey = Object.fromEntries(fieldMappings.value.map(m => [m.arco_field_key, m]))
  return importableFields.value.filter(f => {
    if (f.key === 'vehicle.customer') return profileData.value.track_type === portalStore.trackTypeEnum.VEHICLE_TRANSIT
    if (!f.needs_transcoding) return false
    const mapping = mappingByKey[f.key]
    return mapping && mapping.csv_column_index != null
  })
})

const transcodingColumnsByField = computed(() => Object.fromEntries(
  transcodableFields.value.map(field => {
    const options = getEditorOptionsForField(field)
    return [field.key, [
      ...(field.key === 'vehicle.customer' ? importableFields.value.filter(item => ['subject.name', 'subject.name_ext', 'subject.tax_code', 'subject.vat_number'].includes(item.key)).map(item => ({
        dataField: `criteria.${item.model_field_name || item.key.split('.').pop()}`,
        caption: i18n.t(item.label),
        allowEditing: false,
      })) : [{
        dataField: 'source_value',
        caption: i18n.t('common.sourceValue'),
        allowEditing: false,
      }]),
      {
        dataField: 'target_value',
        caption: i18n.t('common.arcoValue'),
        showEditorAlways: true,
        editCellTemplate: 'targetValueEditor',
        ...(!options.editor && {
          lookup: {
            dataSource: options.dataSource,
            valueExpr: options.valueExpr,
            displayExpr: options.displayExpr,
          },
        }),
      },
    ]]
  })
))

const transcodingEditing = { allowUpdating: true, mode: 'cell' }
const transcodingByField = ref({})
const transcodingError = ref('')
const loadingTranscoding = ref({})
const transcodingSources = new Map()
const transcodingGrids = new Map()
const canSave = computed(() => !saving.value && !Object.values(loadingTranscoding.value).some(Boolean) && transcodableFields.value.every(field => transcodingByField.value[field.key] != null))

const resetTranscoding = () => {
  transcodingError.value = ''
  transcodingByField.value = {}
  loadingTranscoding.value = {}
  transcodingSources.clear()
}

const autoMatchTargetValue = (rawValue, options) => {
  if (!options || options.type !== 'lookup') return null
  const normalized = String(rawValue ?? '').trim().toLowerCase()
  if (!normalized) return null
  const matches = options.dataSource.filter((item) => {
    const code = String(item.code ?? item.value ?? '').toLowerCase()
    const name = String(item.name || item.label || '').toLowerCase()
    return code === normalized || name === normalized
  })
  return matches.length === 1 ? matches[0][options.valueExpr] : null
}

const loadTranscodingRows = async(field, sourceValues) => {
  const mapping = fieldMappings.value.find(row => row.arco_field_key === field.key)
  const ownerMappings = field.key === 'vehicle.customer' ? fieldMappings.value.filter(row => row.arco_field_key.startsWith('subject.') && isFieldMapped(row)).map(row => ({
    arco_field_key: row.arco_field_key,
    csv_column_index: row.csv_column_index,
    fixed_value: row.fixed_value,
    default_value: row.default_value,
  })) : null
  const source = JSON.stringify([ownerMappings || mapping.csv_column_index, fileConfigData.value])
  if (transcodingSources.get(field.key) === source) return

  const fieldsRequest = fieldsRequestId
  const previewRequest = filePreviewRequestId
  const isCurrentRequest = () => fieldsRequest === fieldsRequestId && previewRequest === filePreviewRequestId
  loadingTranscoding.value[field.key] = true
  try {
    const response = sourceValues !== undefined ? { data: { values: sourceValues } } : await registryApi.getImportUniqueValuesAsync({
      uploadedFile: uploadedFile.value,
      fileConfig: fileConfigData.value,
      ownerMappings,
    })
    if (!isCurrentRequest()) return
    const options = getEditorOptionsForField(field)
    const rows = ownerMappings ? response.data.values : response.data.values.map(sourceValue => ({
      source_value: sourceValue,
      target_value: autoMatchTargetValue(sourceValue, options),
    }))
    transcodingByField.value[field.key] = rows
    transcodingSources.set(field.key, source)
  } finally {
    if (isCurrentRequest()) loadingTranscoding.value[field.key] = false
  }
}

const transcodingRowsForSave = computed(() =>
  transcodableFields.value.flatMap(field => {
    return (transcodingByField.value[field.key] || [])
      .filter(r => r.target_value != null)
      .map(r => ({
        arco_field_key: field.key,
        source_value: r.source_value,
        [field.transcoding_kind === 'choice' ? 'target_choice_value' : 'target_object_id']: r.target_value,
      }))
  })
)

const onProfileFieldChanged = async(event) => {
  if (event.dataField !== 'track_type') return
  const searchKeys = portalStore.getLocalizedImportSearchKeys(event.value)
  if (!searchKeys.some(option => option.id === fileConfigData.value.search_key)) {
    fileConfigData.value.search_key = searchKeys[0]?.id ?? null
  }
  const requestId = ++fieldsRequestId
  const trackType = event.value
  importableFields.value = []
  fieldMappings.value = []
  resetTranscoding()
  if (!trackType) {
    loadingFields.value = false
    return
  }
  loadingFields.value = true
  try {
    if (!i18n.te('common.arcoField') || !i18n.te('error.import.config_invalid')) await portalStore.setLanguageAsync(i18n.locale.value, true)
    const response = await registryApi.getImportableFieldsAsync({ track_type: trackType })
    if (requestId !== fieldsRequestId) return
    const fields = response.data
    await loadFieldLookupsAsync(fields)
    if (requestId !== fieldsRequestId) return
    importableFields.value = fields
    const grouped = getMappingLayout(trackType, portalStore.trackTypeEnum).length > 0
    fieldMappings.value = createFieldMappings(getMappingFields(fields), i18n.t, grouped)
  } finally {
    if (requestId === fieldsRequestId) loadingFields.value = false
  }
}

const onFileUploaded = async(e) => {
  const requestId = ++filePreviewRequestId
  const file = e.value?.[0] || null
  if (file !== uploadedFile.value) {
    fileConfigData.value.sheet_name = null
    sheetNames.value = []
  }
  uploadedFile.value = file
  resetTranscoding()
  filePreviewReady.value = false
  loadingFilePreview.value = false
  csvPreviewColumns.value = []
  if (!file || e.component?.option('isValid') === false) return
  loadingFilePreview.value = true
  try {
    const preview = await registryApi.previewImportFileAsync({ uploadedFile: file, fileConfig: fileConfigData.value })
    if (requestId !== filePreviewRequestId) return
    csvPreviewColumns.value = preview.data.columns
    sheetNames.value = preview.data.sheet_names || []
    filePreviewReady.value = true
  } finally {
    if (requestId === filePreviewRequestId) loadingFilePreview.value = false
  }
}

const canGoToStep3 = computed(() => !!profileData.value.track_type && !!uploadedFile.value && isSearchKeyValid.value && filePreviewReady.value && !loadingFilePreview.value)
const canGoToStep4 = computed(() => canGoToStep3.value && !loadingFields.value && fieldMappingRef.value?.isComplete)
const canGoNext = computed(() => ({
  1: !!profileData.value.track_type && !loadingFields.value,
  2: canGoToStep3.value,
  3: canGoToStep4.value,
})[step.value])

const nextStep = async() => {
  if (!canGoNext.value) return
  if (step.value === 3) {
    await fieldMappingRef.value.saveEditData()
    if (!canGoToStep4.value) return
  }
  step.value += 1
  if (step.value === 4) {
    const fields = transcodableFields.value.filter(field => field.key !== 'vehicle.customer')
    loadingTranscoding.value.batch = true
    try {
      const response = fields.length ? await registryApi.getImportUniqueValuesAsync({
        uploadedFile: uploadedFile.value,
        fileConfig: fileConfigData.value,
        csvColumns: Object.fromEntries(fields.map(field => [field.key, fieldMappings.value.find(mapping => mapping.arco_field_key === field.key).csv_column_index])),
      }) : null
      await Promise.all(transcodableFields.value.map(field => loadTranscodingRows(field, response?.data.values[field.key])))
    } catch (error) {
      resetTranscoding()
      throw error
    } finally {
      loadingTranscoding.value.batch = false
    }
  }
}

const onFileConfigChanged = async(event) => {
  const parsingFields = ['file_format', 'csv_delimiter', 'encoding', 'header_row_number', 'sheet_name', 'text_qualifier']
  if (uploadedFile.value && parsingFields.includes(event.dataField)) {
    await onFileUploaded({ value: [uploadedFile.value] })
  }
}

const close = () => {
  fieldsRequestId += 1
  filePreviewRequestId += 1
  step.value = 1
  profileData.value = { operation_type: portalStore.operationTypeEnum.IMPORT, track_type: null }
  uploadedFile.value = null
  filePreviewReady.value = false
  loadingFilePreview.value = false
  loadingFields.value = false
  csvPreviewColumns.value = []
  sheetNames.value = []
  importableFields.value = []
  fieldMappings.value = []
  resetTranscoding()
  emit('update:visible', false)
}

const save = async() => {
  if (!canSave.value) return
  saving.value = true
  try {
    await Promise.all([...transcodingGrids.values()].map(grid => grid.saveEditData()))
    transcodingError.value = ''
    if ((transcodingByField.value['vehicle.customer'] || []).some(row => row.target_value == null)) {
      transcodingError.value = 'common.importOwnerSelectionRequired'
      return
    }
    const mappingsPayload = fieldMappings.value
      .filter(isFieldMapped)
      .map(m => ({
        arco_field_key: m.arco_field_key,
        csv_column_index: m.csv_column_index,
        fixed_value: m.csv_column_index == null ? m.fixed_value : null,
        default_value: m.default_value,
      }))

    const response = await registryApi.createImportExportJobWizardAsync({
      profile: {
        ...profileData.value,
        ...fileConfigData.value,
        profile_name: `${profileData.value.track_type}-${Date.now()}`,
      },
      fieldMappings: mappingsPayload,
      valueTranscodings: transcodingRowsForSave.value,
      job: {
        operation_type: profileData.value.operation_type,
        track_type: profileData.value.track_type,
      },
      sourceFile: uploadedFile.value,
    })

    emit('created', response.data)
    close()
  } finally {
    saving.value = false
  }
}

const popupToolbar = computed(() => {
  const items = [
    {
      widget: 'dxButton', toolbar: 'bottom', location: 'after',
      options: { text: i18n.t('common.cancel'), onClick: close },
    },
  ]

  if (step.value > 1) {
    items.unshift({
      widget: 'dxButton', toolbar: 'bottom', location: 'after',
      options: { text: i18n.t('common.back'), stylingMode: 'text', onClick: () => { step.value -= 1 } },
    })
  }

  if (step.value < 4) {
    items.unshift({
      widget: 'dxButton', toolbar: 'bottom', location: 'after',
      options: {
        text: i18n.t('common.next'),
        type: 'default',
        disabled: !canGoNext.value,
        onClick: nextStep,
      },
    })
  } else {
    items.unshift({
      widget: 'dxButton', toolbar: 'bottom', location: 'after',
      options: { text: i18n.t('common.save'), type: 'default', disabled: !canSave.value, onClick: save },
    })
  }

  return items
})
const fileConfigItems = computed(() => [
  {
    dataField: 'file_format',
    label: { text: i18n.t('common.fileFormat') },
    editorType: 'dxSelectBox',
    editorOptions: {
      ...useLookup({ store: portalStore.getLocalizedImportFileFormats() }),
      placeholder: ''
    }
  },
  {
    dataField: 'sheet_name',
    label: { text: i18n.t('common.sheetName') },
    editorType: 'dxSelectBox',
    editorOptions: { items: sheetNames.value, showClearButton: true },
    visible: fileConfigData.value.file_format !== portalStore.importFileFormatEnum.CSV,
  },
  {
    dataField: 'csv_delimiter',
    label: { text: i18n.t('common.csvDelimiter') },
    editorType: 'dxSelectBox',
    editorOptions: {
      ...useLookup({ store: portalStore.getLocalizedImportCsvDelimiters() }),
      placeholder: ''
    },
    visible: fileConfigData.value.file_format === portalStore.importFileFormatEnum.CSV
  },
  {
    dataField: 'encoding',
    label: { text: i18n.t('common.encoding') },
    editorType: 'dxSelectBox',
    editorOptions: {
      ...useLookup({ store: portalStore.getLocalizedImportEncodings() }),
      placeholder: ''
    }
  },
  {
    dataField: 'date_format',
    label: { text: i18n.t('common.dateFormat') },
    editorType: 'dxSelectBox',
    editorOptions: {
      ...useLookup({ store: portalStore.getLocalizedImportDateFormats() }),
      placeholder: ''
    }
  },
  {
    dataField: 'thousand_separator',
    label: { text: i18n.t('common.thousandSeparator') },
    editorType: 'dxTextBox',
    editorOptions: { maxLength: 5, showClearButton: true }
  },
  {
    dataField: 'text_qualifier',
    label: { text: i18n.t('common.textQualifier') },
    editorType: 'dxSelectBox',
    editorOptions: {
      dataSource: textQualifiers.value,
      valueExpr: 'value',
      displayExpr: 'name'
    }
  },
  {
    dataField: 'decimal_separator',
    label: { text: i18n.t('common.decimalSeparator') },
    editorType: 'dxTextBox',
    editorOptions: { maxLength: 5, showClearButton: true }
  },
  { dataField: 'header_row_number', label: { text: i18n.t('common.headerRowNumber') }, editorType: 'dxNumberBox', editorOptions: { min: 1, showSpinButtons: true }},
  {
    dataField: 'search_key',
    label: { text: i18n.t('common.searchKey') },
    editorType: 'dxSelectBox',
    editorOptions: {
      ...useLookup({ store: searchKeyOptions.value }),
      placeholder: ''
    }
  },
  {
    dataField: 'existing_record_mode',
    label: { text: i18n.t('common.importExistingRecordMode') },
    editorType: 'dxSelectBox',
    editorOptions: {
      ...useLookup({ store: portalStore.getLocalizedImportExistingRecordModes() }),
      placeholder: ''
    }
  },
  {
    dataField: 'update_mode',
    label: { text: i18n.t('common.updateMode') },
    editorType: 'dxSelectBox',
    editorOptions: {
      ...useLookup({ store: portalStore.getLocalizedImportUpdateModes() }),
      placeholder: ''
    }
  },
])
</script>

<template>
  <dx-popup
    :visible="props.visible"
    :drag-enabled="true"
    :resize-enabled="true"
    width="90%"
    height="85%"
    :title="i18n.t('common.newImportExport')"
    :show-title="true"
    :toolbar-items="popupToolbar"
    @update:visible="(v) => !v && close()"
  >
    <template #content>
      <q-stepper
        v-model="step"
        :class="{ 'full-height': step === 3 }"
        :style="step === 3 ? { display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)' } : undefined"
        flat
        animated
      >
        <q-step
          :name="1"
          :title="i18n.t('common.operationAndTrackType')"
          icon="mdi-cog"
          :done="step > 1"
        >
          <dx-form
            :form-data="profileData"
            :col-count="2"
            :items="[
              {
                dataField: 'operation_type',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.operationType') },
                editorOptions: {
                  dataSource: { store: portalStore.getLocalizedOperationTypes().filter(type => type.id === portalStore.operationTypeEnum.IMPORT) },
                  valueExpr: 'id',
                  displayExpr: 'name',
                },
                isRequired: true },
              {
                dataField: 'track_type',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.trackType') },
                editorOptions: {
                  dataSource: { store: portalStore.getLocalizedTrackTypes() },
                  valueExpr: 'id',
                  displayExpr: 'name',
                },
                isRequired: true },
            ]"
            @field-data-changed="onProfileFieldChanged"
          />
        </q-step>

        <q-step
          :name="2"
          :title="i18n.t('common.fileConfiguration')"
          icon="mdi-file-upload"
          :done="step > 2"
        >
          <dx-file-uploader
            :select-button-text="i18n.t('common.selectFile')"
            :max-file-size="20 * 1024 * 1024"
            accept=".csv,.xls,.xlsx"
            upload-mode="useForm"
            @value-changed="onFileUploaded"
          />
          <dx-form
            :form-data="fileConfigData"
            :col-count="3"
            :items="fileConfigItems"
            @field-data-changed="onFileConfigChanged"
          />
        </q-step>

        <q-step
          :name="3"
          class="relative-position full-height"
          :title="i18n.t('common.fieldMapping')"
          icon="mdi-table-arrow-right"
          :done="step > 3"
        >
          <import-field-mapping
            :key="profileData.track_type"
            ref="fieldMappingRef"
            v-model:mappings="fieldMappings"
            class="full-height q-pa-lg"
            :fields="importableFields"
            :columns="csvPreviewColumns"
            :track-type="profileData.track_type"
          />
        </q-step>

        <q-step
          :name="4"
          :title="i18n.t('common.valueTranscoding')"
          icon="mdi-swap-horizontal"
        >
          <div
            v-if="transcodingError"
            class="text-negative q-mb-md"
          >
            {{ i18n.t(transcodingError) }}
          </div>
          <div v-if="transcodableFields.length === 0">
            {{ i18n.t('common.noFieldsToTranscode') }}
          </div>
          <div
            v-for="field in transcodableFields"
            :key="field.key"
            class="q-mb-lg"
          >
            <h6>{{ i18n.t(field.label) }}</h6>

            <div v-if="loadingTranscoding[field.key]">
              {{ i18n.t('common.loading') }}
            </div>

            <dx-data-grid
              v-else
              :data-source="transcodingByField[field.key] || []"
              :columns="transcodingColumnsByField[field.key]"
              :editing="transcodingEditing"
              :repaint-changes-only="true"
              :show-borders="true"
              key-expr="source_value"
              @initialized="event => transcodingGrids.set(field.key, event.component)"
              @disposing="() => transcodingGrids.delete(field.key)"
            >
              <template #targetValueEditor="{ data: cellInfo }">
                <import-field-value-editor
                  :field="field"
                  :cell-info="cellInfo"
                />
              </template>
            </dx-data-grid>
          </div>
        </q-step>
      </q-stepper>
    </template>
  </dx-popup>
</template>
